import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Briefcase,
  Building2,
  Check,
  HeartHandshake,
  Home,
  Mail,
  Megaphone,
  Monitor,
  Rocket,
  Sparkles,
  Store,
  User,
  Users,
} from 'lucide-react';
import { useProspecting } from '../../../context/ProspectingContext';
import { BUSINESS_TYPES, FOR_WHO_OPTIONS, GOALS } from './onboardingConfig';
import { buildIcpFromSetup, updateIcpFacet } from './icpMapper';
import IcpReviewCard from './IcpReviewCard';
import {
  analyzeGuidedProspectingSetup,
} from '../prospectingApi';

const BUSINESS_ICONS = {
  agency: Briefcase,
  saas: Monitor,
  consult: Users,
  service: Home,
  ecom: Store,
  other: Sparkles,
};

const GOAL_ICONS = {
  'cold-outreach': Mail,
  partner: HeartHandshake,
  event: Megaphone,
  investor: BarChart3,
};

export function buildGuidedBootstrapMessage({ setup, icp }) {
  const business = BUSINESS_TYPES.find((item) => item.id === setup?.businessTypeId);
  const goal = GOALS.find((item) => item.id === setup?.goalId);
  const businessDescription = String(setup?.businessDescription || '').trim();

  return [
    'Guided bootstrap: refine prospecting filters for a new search.',
    setup?.forWho === 'client'
      ? 'This search is for a client.'
      : 'This search is for my own business.',
    business?.label ? `Business type: ${business.label}.` : '',
    businessDescription ? `Business description: ${businessDescription}` : '',
    goal?.label ? `Chosen goal: ${goal.label}.` : '',
    icp?.headline ? `Current ICP headline: ${icp.headline}.` : '',
    icp?.summary ? `Current ICP summary: ${icp.summary}` : '',
  ].filter(Boolean).join('\n');
}

export async function resolveGuidedSetupIcp({
  setup,
  fallbackIcp,
  assistantClient = analyzeGuidedProspectingSetup,
}) {
  if (!isValidGuidedIcp(fallbackIcp)) return fallbackIcp;

  try {
    const refinedIcp = await assistantClient({ setup, fallbackIcp });
    if (isValidGuidedIcp(refinedIcp)) {
      return {
        ...fallbackIcp,
        ...refinedIcp,
        setup: fallbackIcp.setup,
      };
    }
  } catch {
    // Keep setup deterministic if guided analysis fails.
  }

  return fallbackIcp;
}

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
      <span className="font-medium text-gray-700 dark:text-gray-200">Step {Math.min(step + 1, 4)}</span>
      <span>of 4</span>
      <div className="ml-1 flex gap-1">
        {Array.from({ length: 4 }).map((_, index) => (
          <span
            key={index}
            className={`h-1 w-7 rounded-full transition-colors ${
              index <= step ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-700'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function OptionCard({ active, icon: Icon, title, description, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative flex min-h-[142px] flex-col gap-3 rounded-2xl border p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg ${
        active
          ? 'border-brand-500 bg-brand-50 shadow-brand-100/70 dark:border-brand-600 dark:bg-brand-900/20 dark:shadow-brand-950/20'
          : 'border-gray-200 bg-white hover:border-brand-200 dark:border-gray-700 dark:bg-gray-800/50 dark:hover:border-brand-800'
      }`}
    >
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${
        active
          ? 'bg-brand-600 text-white'
          : 'bg-brand-50 text-brand-700 group-hover:bg-brand-100 dark:bg-brand-900/30 dark:text-brand-300'
      }`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-sm font-semibold text-gray-950 dark:text-white">{title}</div>
        <div className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{description}</div>
      </div>
      {active && (
        <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-white">
          <Check className="h-3 w-3" strokeWidth={3} />
        </span>
      )}
    </button>
  );
}

function NavRow({ back, children }) {
  return (
    <div className="flex items-center justify-between gap-3 pt-1">
      <div>
        {back && (
          <button
            type="button"
            onClick={back}
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
}

function ContinueButton({ disabled, onClick, children = 'Continue', icon: Icon = ArrowRight }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
      <Icon className="h-4 w-4" />
    </button>
  );
}

function ThinkingStep({ setup }) {
  const [doneCount, setDoneCount] = useState(0);
  const business = BUSINESS_TYPES.find((item) => item.id === setup.businessTypeId);
  const goal = GOALS.find((item) => item.id === setup.goalId);
  const lines = [
    `Analyzed "${business?.label || 'your business'}" with your custom description`,
    `Matched intent for "${goal?.label || 'your goal'}"`,
    'Building your live preview',
  ];

  useEffect(() => {
    setDoneCount(0);
    const timers = [600, 1350, 2100].map((delay, index) => (
      setTimeout(() => setDoneCount(index + 1), delay)
    ));
    return () => timers.forEach(clearTimeout);
  }, [setup.businessTypeId, setup.goalId]);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
      <div className="mb-5 flex items-center gap-3">
        <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
          <Sparkles className="h-5 w-5" />
          <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-brand-500 animate-ping" />
        </div>
        <div>
          <div className="text-sm font-semibold text-gray-950 dark:text-white">Able is building your prospect profile</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Turning the choices into filters, columns, and a preview.</div>
        </div>
      </div>
      <div className="space-y-3">
        {lines.map((line, index) => {
          const done = index < doneCount;
          return (
            <div key={line} className="flex items-center gap-3 text-sm">
              <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${
                done
                  ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                  : 'border-2 border-brand-500 border-t-transparent animate-spin'
              }`}>
                {done && <Check className="h-3 w-3" strokeWidth={3} />}
              </span>
              {done ? (
                <span className="text-gray-700 dark:text-gray-200">{line}</span>
              ) : (
                <span className="h-3 w-2/3 max-w-md rounded-full bg-gradient-to-r from-gray-100 via-brand-50 to-gray-100 bg-[length:200%_100%] animate-pulse dark:from-gray-800 dark:via-brand-900/30 dark:to-gray-800" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ProspectingGuidedSetup() {
  const navigate = useNavigate();
  const { createSearchFromIcp } = useProspecting();
  const [step, setStep] = useState(0);
  const [setup, setSetup] = useState({
    forWho: null,
    businessTypeId: null,
    businessDescription: '',
    goalId: null,
  });
  const [reviewIcp, setReviewIcp] = useState(null);

  const generatedIcp = useMemo(() => (
    setup.businessTypeId && setup.goalId ? buildIcpFromSetup(setup) : null
  ), [setup]);

  useEffect(() => {
    if (step !== 3 || !generatedIcp) return undefined;
    let cancelled = false;
    let transitionTimer;

    const run = async () => {
      const startedAt = Date.now();
      const resolved = await resolveGuidedSetupIcp({
        setup,
        fallbackIcp: generatedIcp,
      });
      const remainingDelay = Math.max(0, 1900 - (Date.now() - startedAt));
      transitionTimer = setTimeout(() => {
        if (cancelled) return;
        setReviewIcp(resolved);
        setStep(4);
      }, remainingDelay);
    };

    run();
    return () => {
      cancelled = true;
      if (transitionTimer) clearTimeout(transitionTimer);
    };
  }, [generatedIcp, setup, step]);

  const updateSetup = (patch) => {
    setSetup((current) => ({ ...current, ...patch }));
  };

  const createSearch = (openTableOnStart) => {
    const finalIcp = reviewIcp || generatedIcp;
    if (!finalIcp) return;
    const finalFilters = finalIcp.suggestedFilters || [];
    const id = createSearchFromIcp({
      name: finalIcp.headline,
      searchType: finalIcp.searchType,
      setup: finalIcp.setup,
      icp: finalIcp,
      filters: finalFilters,
      openTableOnStart,
    });
    navigate(`/prospecting/${id}`);
  };

  const title = [
    "Let's set you up. Who are these leads for?",
    'What kind of business is it?',
    'What do you want to do with these leads?',
    'Building your search',
    "Here's what I built",
  ][step];

  return (
    <main className="min-h-screen overflow-y-auto bg-gray-50 px-4 py-8 dark:bg-gray-950 sm:px-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-brand-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-700 dark:border-brand-800 dark:bg-gray-900 dark:text-brand-300">
              <Sparkles className="h-3.5 w-3.5" />
              New prospecting search
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-gray-950 dark:text-white">
              {title}
            </h1>
          </div>
          {step < 4 && <Stepper step={step} />}
        </div>

        {step === 0 && (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {FOR_WHO_OPTIONS.map((option) => (
                <OptionCard
                  key={option.id}
                  active={setup.forWho === option.id}
                  icon={option.id === 'self' ? User : Users}
                  title={option.label}
                  description={option.description}
                  onClick={() => updateSetup({ forWho: option.id })}
                />
              ))}
            </div>
            <NavRow>
              <ContinueButton disabled={!setup.forWho} onClick={() => setStep(1)} />
            </NavRow>
          </>
        )}

        {step === 1 && (
          <>
            <p className="max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
              {setup.forWho === 'client'
                ? "Pick what's closest to your client. You can refine it in your own words below."
                : "Pick what's closest. You can refine it in your own words below."}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {BUSINESS_TYPES.map((business) => (
                <OptionCard
                  key={business.id}
                  active={setup.businessTypeId === business.id}
                  icon={BUSINESS_ICONS[business.id] || Building2}
                  title={business.label}
                  description={business.description}
                  onClick={() => updateSetup({ businessTypeId: business.id })}
                />
              ))}
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Sparkles className="h-4 w-4 text-brand-500" />
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  Or describe {setup.forWho === 'client' ? 'your client' : 'it'} in your own words
                </div>
                <span className="text-xs text-gray-400">Optional</span>
              </div>
              <textarea
                value={setup.businessDescription}
                onChange={(event) => updateSetup({ businessDescription: event.target.value })}
                placeholder={setup.forWho === 'client'
                  ? 'They sell HR software to companies of 50-500 people in Scandinavia. Their best customers are HR Directors who hate manual onboarding...'
                  : "I run a bookkeeping practice in Copenhagen, mostly for dental clinics. Looking for clinic owners who're frustrated with their current accountant..."}
                className="min-h-[92px] w-full resize-y rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm leading-6 text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-brand-300 focus:bg-white focus:ring-2 focus:ring-brand-100 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-brand-700 dark:focus:bg-gray-900 dark:focus:ring-brand-900/30"
              />
            </div>
            <NavRow back={() => setStep(0)}>
              <ContinueButton
                disabled={!setup.businessTypeId && !setup.businessDescription.trim()}
                onClick={() => {
                  if (!setup.businessTypeId) updateSetup({ businessTypeId: 'other' });
                  setStep(2);
                }}
              />
            </NavRow>
          </>
        )}

        {step === 2 && (
          <>
            <p className="max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
              The same business can need very different leads. Pick what you want to do right now.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {GOALS.map((goal) => (
                <OptionCard
                  key={goal.id}
                  active={setup.goalId === goal.id}
                  icon={GOAL_ICONS[goal.id] || Rocket}
                  title={goal.label}
                  description={goal.description}
                  onClick={() => updateSetup({ goalId: goal.id })}
                />
              ))}
            </div>
            {generatedIcp && (
              <div className="flex gap-3 rounded-2xl border border-brand-200 bg-brand-50 p-4 text-sm leading-6 text-brand-900 dark:border-brand-800 dark:bg-brand-900/20 dark:text-brand-100">
                <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-600 dark:text-brand-300" />
                <div>
                  <span className="font-semibold text-brand-700 dark:text-brand-300">Good combo. </span>
                  I would start with {generatedIcp.headline.toLowerCase()} and prepare {generatedIcp.suggestedAiColumns.length} AI columns for the table.
                </div>
              </div>
            )}
            <NavRow back={() => setStep(1)}>
              <ContinueButton
                disabled={!setup.goalId}
                icon={Sparkles}
                onClick={() => setStep(3)}
              >
                Build my search
              </ContinueButton>
            </NavRow>
          </>
        )}

        {step === 3 && <ThinkingStep setup={setup} />}

        {step === 4 && reviewIcp && (
          <IcpReviewCard
            icp={reviewIcp}
            onFacetChange={(facetKey, value) => setReviewIcp((current) => updateIcpFacet(current, facetKey, value))}
            onPreview={() => createSearch(false)}
            onBuildList={() => createSearch(true)}
          />
        )}
      </div>
    </main>
  );
}

function isValidGuidedIcp(icp) {
  if (!icp || typeof icp !== 'object') return false;
  if (!['people', 'companies'].includes(icp.searchType)) return false;
  if (!String(icp.headline || '').trim()) return false;
  if (!String(icp.summary || '').trim()) return false;
  if (!Array.isArray(icp.facets) || icp.facets.length === 0) return false;
  if (!Array.isArray(icp.suggestedFilters)) return false;
  if (!Array.isArray(icp.suggestedAiColumns)) return false;
  return true;
}
